import React, { useState } from 'react'
import { Collapse, Tooltip } from '@mui/material'

import { Chip, Icon, ScoreRating, Typography } from '~components/internal'
import { lightTokens, typography } from '~tokens/internal'
import { type AIInteractionTranscript } from '~types'

import { EVALUATION_TOOLTIPS } from '../../constants/evaluationTooltips'
import {
  formatEnumLabel,
  getCategoryBackgroundColor,
  getCategoryColor,
  getSentimentChipConfig,
} from '../../utils/evaluationHelpers'

import './AIInteractionLogEvaluation.scss'

interface AIInteractionLogEvaluationProps {
  evaluation: AIInteractionTranscript['ai_interaction_evaluation']
}

export const AIInteractionLogEvaluation: React.FC<AIInteractionLogEvaluationProps> = ({
  evaluation,
}) => {
  const [expanded, setExpanded] = useState(true)

  if (!evaluation) {
    return null
  }

  const collapseIconClass = [
    'interaction-log-eval__collapse-icon',
    !expanded && 'interaction-log-eval__collapse-icon--collapsed',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className='interaction-log-eval'>
      <div className='interaction-log-eval__header' onClick={() => setExpanded((prev) => !prev)}>
        <div className='interaction-log-eval__header-left'>
          <Icon name='AutoAwesome' size={typography.fontSizePx.lg} />
          <Typography variant='subtitle1' className='interaction-log-eval__title'>
            AI Evaluation
          </Typography>
        </div>
        <Icon
          name='KeyboardArrowDown'
          size={typography.fontSizePx.xl}
          className={collapseIconClass}
        />
      </div>

      <Collapse in={expanded}>
        <div className='interaction-log-eval__content'>
          {evaluation.category && (
            <div className='interaction-log-eval__metric'>
              <Tooltip title={EVALUATION_TOOLTIPS.category} placement='top' enterDelay={300}>
                <Typography
                  variant='body2'
                  component='span'
                  className='interaction-log-eval__metric-label'
                >
                  Category
                </Typography>
              </Tooltip>
              <div className='interaction-log-eval__metric-value'>
                <Chip
                  label={formatEnumLabel(evaluation.category)}
                  size='small'
                  className='interaction-log-eval__category-chip'
                  style={
                    {
                      '--chip-bg': getCategoryBackgroundColor(evaluation.category),
                      '--chip-color': getCategoryColor(evaluation.category),
                    } as React.CSSProperties
                  }
                />
              </div>
            </div>
          )}

          {evaluation.summary && (
            <div className='interaction-log-eval__metric'>
              <Tooltip title={EVALUATION_TOOLTIPS.summary} placement='top' enterDelay={300}>
                <Typography
                  variant='body2'
                  component='span'
                  className='interaction-log-eval__metric-label'
                >
                  Summary
                </Typography>
              </Tooltip>
              <Typography variant='body2' className='interaction-log-eval__summary-text'>
                {evaluation.summary}
              </Typography>
            </div>
          )}

          <hr className='interaction-log-eval__divider' />

          <div className='interaction-log-eval__metrics-row'>
            {evaluation.outcome && (
              <div className='interaction-log-eval__metric'>
                <Tooltip title={EVALUATION_TOOLTIPS.outcome} placement='top' enterDelay={300}>
                  <Typography
                    variant='body2'
                    component='span'
                    className='interaction-log-eval__metric-label'
                  >
                    Outcome
                  </Typography>
                </Tooltip>
                <div className='interaction-log-eval__metric-value'>
                  <Chip label={formatEnumLabel(evaluation.outcome)} size='small' />
                </div>
              </div>
            )}

            {evaluation.handled !== null && evaluation.handled !== undefined && (
              <div className='interaction-log-eval__metric'>
                <Tooltip title={EVALUATION_TOOLTIPS.handled} placement='top' enterDelay={300}>
                  <Typography
                    variant='body2'
                    component='span'
                    className='interaction-log-eval__metric-label'
                  >
                    Handled
                  </Typography>
                </Tooltip>
                <div className='interaction-log-eval__metric-value'>
                  {evaluation.handled ? (
                    <Icon
                      name='CheckCircle'
                      size={typography.fontSizePx.xl}
                      color={lightTokens.success.main}
                    />
                  ) : (
                    <Icon
                      name='DoNotDisturbOn'
                      size={typography.fontSizePx.xl}
                      color={lightTokens.text.secondary}
                    />
                  )}
                </div>
              </div>
            )}

            {evaluation.rating !== null && evaluation.rating !== undefined && (
              <div className='interaction-log-eval__metric'>
                <Tooltip title={EVALUATION_TOOLTIPS.score} placement='top' enterDelay={300}>
                  <Typography
                    variant='body2'
                    component='span'
                    className='interaction-log-eval__metric-label'
                  >
                    Score
                  </Typography>
                </Tooltip>
                <div className='interaction-log-eval__metric-value'>
                  <ScoreRating rating={evaluation.rating} />
                </div>
              </div>
            )}

            {evaluation.sentiment && (
              <div className='interaction-log-eval__metric'>
                <Tooltip title={EVALUATION_TOOLTIPS.sentiment} placement='top' enterDelay={300}>
                  <Typography
                    variant='body2'
                    component='span'
                    className='interaction-log-eval__metric-label'
                  >
                    Sentiment
                  </Typography>
                </Tooltip>
                <div className='interaction-log-eval__metric-value'>
                  {((): React.ReactNode => {
                    const config = getSentimentChipConfig(evaluation.sentiment)
                    return (
                      <Chip
                        icon={<Icon name={config.iconName} size={typography.fontSizePx.lg} />}
                        label={formatEnumLabel(evaluation.sentiment)}
                        size='small'
                        className='interaction-log-eval__sentiment-chip'
                        style={
                          {
                            '--chip-bg': config.backgroundColor,
                            '--chip-color': config.color,
                          } as React.CSSProperties
                        }
                      />
                    )
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>
      </Collapse>
    </div>
  )
}

export default AIInteractionLogEvaluation
